import type { BannerContent } from "./global-banner.types";

/**
 * Gets the appropriate banner content based on current local time
 * @param now - Current date (defaults to new Date() for easier testing)
 * @returns Banner content configuration or null if no active promotion
 */
export function getBannerContent(now: Date = new Date()): BannerContent | null {
  const year = now.getFullYear();
  const currentTime = now.getTime();

  const isInRange = (
    startMonth: number,
    startDay: number,
    startHour: number,
    startMinute: number,
    endMonth: number,
    endDay: number,
    endHour: number,
    endMinute: number,
  ) => {
    const start = new Date(year, startMonth, startDay, startHour, startMinute);
    const end = new Date(year, endMonth, endDay, endHour, endMinute);

    return currentTime >= start.getTime() && currentTime <= end.getTime();
  };

  // Standard shipping: 15th Dec EOD - 21st Dec 23:59 (except 18th Dec special)
  const isStandardShippingPeriod = isInRange(11, 15, 0, 0, 11, 21, 23, 59);

  // 18th Dec special: 24 hours only - Free Next Day Delivery (takes priority)
  const isFreeDeliveryPeriod = isInRange(11, 18, 7, 0, 11, 18, 23, 59);

  // Next day shipping: 21st Dec 00:00 - 23rd Dec 12:00
  const isNextDayShippingPeriod = isInRange(11, 21, 0, 0, 11, 23, 12, 0);

  const isEndOfSeasonSalePeriod = isInRange(11, 22, 7, 0, 11, 24, 23, 59);

  const isSeasonsGreetingsPeriod = isInRange(11, 25, 0, 0, 11, 25, 23, 59);

  const isBoxingDayPeriod = isInRange(11, 26, 0, 0, 11, 26, 23, 59);

  // Sale Continues: 27th Dec 00:00 - 28th Dec 23:59
  const isSaleContinuesPeriod = isInRange(11, 27, 0, 0, 11, 28, 23, 59);

  // Last Chance: 28th Dec 00:00 - 30th Dec 16:00 (takes priority over Sale Continues on 28th)
  const isLastChancePeriod = isInRange(11, 28, 0, 0, 11, 30, 16, 0);

  const isNewYearPeriod = isInRange(0, 1, 0, 0, 0, 1, 23, 59);

  if (isNewYearPeriod) {
    return {
      title: "HAPPY NEW YEAR",
      subtitle: "10% OFF SALE",
      text: "USE CODE",
      code: "TOP26",
      colorScheme: "christmas",
      clickable: false,
    };
  }

  if (isLastChancePeriod) {
    return {
      title: "LAST CHANCE",
      subtitle: "Free Next Day Delivery (use code: NEXTDAY)",
      text: "Offer Ends 4pm Tues 30th Dec",
      code: "",
      colorScheme: "christmas",
      clickable: false,
    };
  }

  if (isSaleContinuesPeriod) {
    return {
      title: "SALE CONTINUES",
      subtitle: "SHOP OUR SELECTION OF SEASONAL OFFERS",
      text: "",
      code: "",
      colorScheme: "christmas",
      clickable: true,
    };
  }

  if (isBoxingDayPeriod) {
    return {
      title: "HAPPY BOXING DAY!",
      subtitle: "EXCLUSIVE: EXTRA 10% OFF (24 HOURS ONLY)",
      text: "USE CODE",
      code: "BOX10",
      colorScheme: "christmas",
      clickable: false,
    };
  }

  if (isSeasonsGreetingsPeriod) {
    return {
      title: "EXTENDED RETURNS",
      subtitle: "SEASON'S GREETINGS!",
      text: "SALE CONTINUES",
      code: "",
      colorScheme: "christmas",
      clickable: true,
    };
  }

  if (isEndOfSeasonSalePeriod) {
    return {
      title: "EXTENDED RETURNS",
      subtitle: "END OF SEASON SALE",
      text: "SHOP OUR SELECTION OF SEASONAL OFFERS",
      code: "",
      colorScheme: "christmas",
      clickable: true,
    };
  }

  if (isNextDayShippingPeriod) {
    return {
      title: "EXTENDED RETURNS",
      subtitle: "CHRISTMAS DELIVERY: ORDER BY 8PM 23RD DEC (NEXT DAY DELIVERY)",
      text: "",
      code: "",
      colorScheme: "christmas",
      clickable: true,
    };
  }

  if (isFreeDeliveryPeriod) {
    return {
      title: "EXTENDED RETURNS",
      subtitle: "24 HOURS ONLY: FREE NEXT DAY DELIVERY",
      text: "USE CODE",
      code: "TOP25",
      colorScheme: "christmas",
      clickable: true,
    };
  }

  if (isStandardShippingPeriod) {
    return {
      title: "EXTENDED RETURNS",
      subtitle: "CHRISTMAS DELIVERY: ORDER BY 21ST DEC (STANDARD SERVICE)",
      text: "",
      code: "",
      colorScheme: "christmas",
      clickable: true,
    };
  }

  return null;
}
